import { Button } from '@workspace/ui/components/button'
import { ProfileSection } from '@workspace/ui/components/profile-section'
import { ExternalLink, Link, Plus } from 'lucide-react'
import { CopyLinkBanner } from '@/components/copy-link-banner'
import { DangerZoneBanner } from '@/components/danger-zone-banner'
import {
  useCreateStartupDataRoomMutation,
  useDataRoomAnalyticsQuery,
  useRevokeStartupDataRoomMutation,
  useStartupDataRoomQuery,
} from '@/hooks/api'
import { AnalyticsDashboard } from './analytics-dashboard'

interface StartupShareContentProps {
  startupId: string
}

export function StartupShareContent({ startupId }: StartupShareContentProps) {
  const { data: shareLinkResponse } = useStartupDataRoomQuery(startupId)
  const shareLink = shareLinkResponse?.data?.dataRoom
  const isActive = !!shareLink && !shareLink.revokedAt

  const { data: analyticsResponse } = useDataRoomAnalyticsQuery(
    isActive ? shareLink.token : undefined
  )
  const analytics = analyticsResponse?.data

  const { mutate: createShareLink, isPending: isCreating } = useCreateStartupDataRoomMutation()
  const { mutate: revokeShareLink, isPending: isRevoking } = useRevokeStartupDataRoomMutation()

  const shareUrl = isActive ? `${window.location.origin}/s/${shareLink.token}` : null

  const handleCreate = () => {
    createShareLink({ path: { startupId } })
  }

  const handleRevoke = () => {
    revokeShareLink({ path: { startupId } })
  }

  return (
    <div className="space-y-8 py-8">
      <ProfileSection title="Share Link">
        {shareUrl ? (
          <>
            <CopyLinkBanner
              shareUrl={shareUrl}
              description="Anyone with this link can view your profile and latest published pitch"
              actions={
                <Button
                  variant="outline"
                  size="sm"
                  asChild
                  className="shrink-0 border-emerald-500/20 hover:bg-emerald-500/10"
                >
                  <a href={shareUrl} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="size-4" />
                    Preview
                  </a>
                </Button>
              }
            />

            {analytics && (
              <ProfileSection title="Analytics">
                <AnalyticsDashboard analytics={analytics} />
              </ProfileSection>
            )}

            <DangerZoneBanner
              title="Revoke Link"
              description="Permanently deactivate this link. Investors will no longer be able to access your profile through it."
              actionLabel="Revoke"
              pendingLabel="Revoking..."
              onConfirm={handleRevoke}
              isPending={isRevoking}
            />
          </>
        ) : (
          <div className="rounded-2xl border border-border bg-input/50 p-12 flex flex-col items-center text-center">
            <div className="flex items-center justify-center size-16 rounded-2xl bg-elevated mb-5">
              <Link className="size-7 text-muted-foreground" />
            </div>
            <h2 className="text-lg font-semibold text-white mb-1">No share link yet</h2>
            <p className="text-sm text-muted-foreground max-w-sm mb-6">
              Create a SeedForge Link to share your startup profile and latest published pitch with
              investors.
            </p>
            <Button onClick={handleCreate} disabled={isCreating}>
              <Plus className="size-4" />
              {isCreating ? 'Creating...' : 'Create Link'}
            </Button>
          </div>
        )}
      </ProfileSection>
    </div>
  )
}
